import { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeft, DeviceMobile, Trash, WarningCircle } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { EmptyState, ErrorState, LoadingState } from '../../components/PageStates';
import { StatusTag } from '../../components/StatusTag';
import { TierBadge } from '../../components/TierBadge';
import { VND } from '../../format';
import { tokens } from '../../theme';
import { tierApi, tierKeys } from '../tiers/api';
import {
  ENTRY_TYPE_LABELS,
  GENDER_LABELS,
  STATUS_LABELS,
  STATUS_TONE,
  VOUCHER_STATE_LABELS,
  VOUCHER_STATE_TONE,
  displayName,
  formatDob,
  formatPhoneNational,
  formatVnDate,
  formatVnDateTime,
  memberKeys,
  membersApi,
  type LedgerEntryRow,
  type VoucherRow,
} from './api';
import { ChangePhoneModal } from './ChangePhoneModal';
import { EraseMemberModal } from './EraseMemberModal';
import { SuperAdminGate } from './SuperAdminGate';

export function MemberProfilePage() {
  return (
    <SuperAdminGate title="Hồ sơ thành viên">
      <MemberProfileBody />
    </SuperAdminGate>
  );
}

function MemberProfileBody() {
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId: string }>();
  const [changingPhone, setChangingPhone] = useState(false);
  const [erasing, setErasing] = useState(false);

  const profileQuery = useQuery({
    queryKey: memberKeys.profile(memberId ?? ''),
    queryFn: () => membersApi.profile360(memberId!),
    enabled: Boolean(memberId),
  });

  const tiersQuery = useQuery({
    queryKey: tierKeys.all,
    queryFn: tierApi.list,
  });

  const tierName = useMemo(() => {
    const current = (tiersQuery.data ?? []).find((v) => v.isCurrentlyEffective);
    const map = new Map((current?.levels ?? []).map((l) => [l.code, l.name]));
    return (code: string | null) => (code ? (map.get(code) ?? code) : 'Chưa có hạng');
  }, [tiersQuery.data]);

  const member = profileQuery.data;

  const ledgerColumns: ColumnsType<LedgerEntryRow> = [
    {
      title: 'Thời điểm',
      dataIndex: 'createdAt',
      width: 130,
      render: (iso: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatVnDateTime(iso)}</span>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'entryType',
      width: 150,
      render: (_, row) => ENTRY_TYPE_LABELS[row.entryType],
    },
    {
      title: 'Nội dung',
      key: 'note',
      render: (_, row) => (
        <>
          <div>{row.note?.trim() || row.sourceType}</div>
          {row.note && (
            <div style={{ fontSize: 12, color: tokens.color.ink3 }}>{row.sourceType}</div>
          )}
        </>
      ),
    },
    {
      title: 'Điểm dùng được',
      dataIndex: 'spendableDelta',
      width: 140,
      align: 'right',
      render: (delta: number) => <SignedPoints value={delta} />,
    },
    {
      title: 'Điểm xét hạng',
      dataIndex: 'tierDelta',
      width: 130,
      align: 'right',
      render: (delta: number) => <SignedPoints value={delta} />,
    },
  ];

  const voucherColumns: ColumnsType<VoucherRow> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 140,
      render: (code: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12.5 }}>{code}</span>
      ),
    },
    {
      title: 'Quà',
      dataIndex: 'title',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      width: 130,
      render: (_, row) => (
        <StatusTag color={VOUCHER_STATE_TONE[row.state]}>{VOUCHER_STATE_LABELS[row.state]}</StatusTag>
      ),
    },
    {
      title: 'Điểm đã trả',
      dataIndex: 'pointsPaid',
      width: 120,
      align: 'right',
      render: (points: number) => VND.format(points),
    },
    {
      title: 'Hết hạn',
      dataIndex: 'expiresAt',
      width: 120,
      render: (iso: string) => formatVnDate(iso),
    },
  ];

  return (
    <>
      <PageHeader
        title={member ? displayName(member.name) : 'Hồ sơ thành viên'}
        subtitle={member ? `Thành viên · ${member.memberCode}` : undefined}
        actions={
          <Space>
            <Button icon={<ArrowLeft size={14} aria-hidden />} onClick={() => navigate('/members')}>
              Về danh sách
            </Button>
            {member && member.status !== 'Anonymized' && (
              <Button
                icon={<DeviceMobile size={14} aria-hidden />}
                onClick={() => setChangingPhone(true)}
              >
                Đổi số điện thoại
              </Button>
            )}
            {member && member.status !== 'Anonymized' && (
              <Button danger icon={<Trash size={14} aria-hidden />} onClick={() => setErasing(true)}>
                Xóa tài khoản
              </Button>
            )}
          </Space>
        }
      />
      <PageBody>
        {!memberId && <ErrorState error={new Error('Thiếu mã thành viên.')} />}
        {profileQuery.isLoading && <LoadingState rows={8} />}
        {profileQuery.isError && (
          <ErrorState error={profileQuery.error} onRetry={() => profileQuery.refetch()} />
        )}

        {member && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: tokens.space.sectionGap,
              alignItems: 'start',
            }}
          >
            <div
              style={{
                flex: '0 1 360px',
                minWidth: 280,
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space.sectionGap,
              }}
            >
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                  <span
                    aria-hidden
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: tokens.radius.pill,
                      background: tokens.color.green50,
                      color: tokens.color.green,
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      fontSize: 18,
                    }}
                  >
                    {initialsOf(displayName(member.name))}
                  </span>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{displayName(member.name)}</div>
                    <div
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 12.5,
                        color: tokens.color.ink2,
                        marginTop: 3,
                      }}
                    >
                      {formatPhoneNational(member.phone)}
                    </div>
                  </div>
                </div>
                <Space wrap size={8} style={{ marginBottom: 16 }}>
                  <StatusTag color={STATUS_TONE[member.status]}>
                    {STATUS_LABELS[member.status]}
                  </StatusTag>
                  <TierBadge code={member.tierCode} name={tierName(member.tierCode)} />
                  {member.tierSince && (
                    <span style={{ fontSize: 12, color: tokens.color.ink3 }}>
                      từ {formatVnDate(member.tierSince)}
                    </span>
                  )}
                </Space>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Điểm dùng được">
                    <Typography.Text strong style={{ color: tokens.color.green }}>
                      {VND.format(member.spendablePoints)}
                    </Typography.Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Tổng sổ cái">
                    {VND.format(member.ledgerSum)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Điểm xét hạng">
                    {VND.format(member.tierPoints)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Điểm xét hạng trong chu kỳ">
                    {VND.format(member.tierPointsInCycle)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tổng chi tiêu">
                    {VND.format(member.billScanTotalAmount)}đ
                  </Descriptions.Item>
                  <Descriptions.Item label="Số lần tới ăn">
                    {VND.format(member.billScanCount)} lần
                  </Descriptions.Item>
                  <Descriptions.Item label="Hoạt động gần nhất">
                    {member.lastActivityAt ? formatVnDateTime(member.lastActivityAt) : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ngày sinh">
                    {member.dateOfBirth ? formatDob(member.dateOfBirth) : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Giới tính">
                    {member.gender ? GENDER_LABELS[member.gender] : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ngày tạo">
                    {formatVnDateTime(member.createdAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Đồng ý xử lý dữ liệu">
                    {member.consentedAt ? formatVnDateTime(member.consentedAt) : 'Chưa đồng ý'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {!member.balanceMatchesLedger && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningCircle size={20} weight="fill" aria-hidden />}
                  message="Số dư không khớp sổ cái"
                  description={`Điểm dùng được đang hiện ${VND.format(member.spendablePoints)}, tổng sổ cái ${VND.format(member.ledgerSum)}. Đây là gợi ý điều tra, không phải phép nghiệm thu đối chiếu.`}
                />
              )}

              <Card title="Thao tác đặc quyền">
                {member.status === 'Anonymized' ? (
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Tài khoản đã ẩn danh. Không đổi số điện thoại.
                  </Typography.Paragraph>
                ) : (
                  <Button
                    block
                    icon={<DeviceMobile size={16} aria-hidden />}
                    onClick={() => setChangingPhone(true)}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    Đổi số điện thoại
                  </Button>
                )}
              </Card>
            </div>

            <div
              style={{
                flex: '1 1 480px',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.space.sectionGap,
              }}
            >
              <Card
                styles={{ body: { padding: 0 } }}
                title="Sổ điểm"
                extra={
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    20 dòng gần nhất · ghi thêm, không sửa dòng cũ
                  </Typography.Text>
                }
              >
                {member.recentLedger.length === 0 ? (
                  <div style={{ padding: tokens.space.cardPadding }}>
                    <EmptyState message="Chưa có bút toán điểm." />
                  </div>
                ) : (
                  <DataTable<LedgerEntryRow>
                    rowKey="id"
                    columns={ledgerColumns}
                    dataSource={member.recentLedger}
                    toolbar={false}
                    pagination={false}
                    searchable={false}
                  />
                )}
              </Card>

              <Card styles={{ body: { padding: 0 } }} title="Ví voucher">
                {member.vouchers.length === 0 ? (
                  <div style={{ padding: tokens.space.cardPadding }}>
                    <EmptyState message="Ví chưa có voucher." />
                  </div>
                ) : (
                  <DataTable<VoucherRow>
                    rowKey="id"
                    columns={voucherColumns}
                    dataSource={member.vouchers}
                    toolbar={false}
                    pagination={false}
                    searchable={false}
                  />
                )}
              </Card>
            </div>
          </div>
        )}
      </PageBody>
      {changingPhone && member && (
        <ChangePhoneModal member={member} onClose={() => setChangingPhone(false)} />
      )}
      {erasing && member && <EraseMemberModal member={member} onClose={() => setErasing(false)} />}
    </>
  );
}

function SignedPoints({ value }: { value: number }) {
  const sign = value > 0 ? '+' : '';
  const color =
    value > 0 ? tokens.color.ok : value < 0 ? tokens.color.ink : tokens.color.ink3;
  return (
    <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {sign}
      {VND.format(value)}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(-2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
